import { z } from 'zod';
import { TimestampSchema } from '../common/clock.js';
import { EventIdSchema, newId, WebhookDeliveryIdSchema, WebhookSubscriptionIdSchema, } from '../common/ids.js';
import { defineStateMachine } from '../common/state-machine.js';
import { TenantCodeSchema } from '../common/tenant.js';
import { EVENT_CATALOG, EventTypeSchema } from './catalog.js';
/** Sobre de evento (envelope) — `05-eventos-webhooks.md` §1. */
export const EventEnvelopeSchema = z
    .object({
    id: EventIdSchema,
    type: EventTypeSchema,
    version: z.int().min(1),
    tenantId: TenantCodeSchema,
    occurredAt: TimestampSchema,
    traceId: z.string().nullable(),
    subject: z.object({ type: z.string(), id: z.string() }),
    data: z.record(z.string(), z.unknown()),
})
    .meta({ id: 'EventEnvelope' });
/** Construye y valida un evento listo para el outbox (misma transacción que el cambio de estado). */
export function buildEvent(type, data, options) {
    const definition = EVENT_CATALOG[type];
    const parsed = definition.data.parse(data);
    return {
        id: newId('event', options.clock).publicId,
        type,
        version: definition.version,
        tenantId: options.tenantCode,
        occurredAt: options.clock.now().toISOString(),
        traceId: options.traceId ?? null,
        subject: { type: definition.subjectType, id: options.subjectId },
        data: parsed,
    };
}
/** Valida un sobre recibido (consumidores internos / receptores de webhooks). */
export function parseEvent(input) {
    const envelope = EventEnvelopeSchema.parse(input);
    const data = EVENT_CATALOG[envelope.type].data.parse(envelope.data);
    return { ...envelope, data };
}
// ── Webhooks salientes ────────────────────────────────────────────────────
export const WEBHOOK_HEADERS = {
    eventId: 'Paguz-Event-Id',
    eventType: 'Paguz-Event-Type',
    signature: 'Paguz-Signature',
};
/**
 * Firma: `Paguz-Signature: t=<unix>,v1=<hex>[,v1=<hex>]` con `v1 = HEX(HMAC_SHA256(secret, "{t}.{rawBody}"))`.
 * El receptor recalcula sobre el cuerpo crudo, compara en tiempo constante y rechaza si `|now − t| > 300 s`.
 * Durante rotación se envían dos `v1` (el secreto anterior vale 24 h). Implementación: `@paguz/server-kit`.
 */
export const WEBHOOK_SIGNATURE_TOLERANCE_SECONDS = 300;
export const WEBHOOK_SECRET_ROTATION_GRACE_SECONDS = 24 * 60 * 60;
export const WEBHOOK_DELIVERY_TIMEOUT_MS = 10_000;
/** Backoff con jitter: 30 s, 2 min, 10 min, 30 min, 1 h, 2 h, 4 h, 8 h… hasta 24 h desde el primer intento. */
export const WEBHOOK_RETRY_SCHEDULE_SECONDS = [
    30, 120, 600, 1800, 3600, 7200, 14_400, 28_800,
];
export const WEBHOOK_MAX_RETRY_WINDOW_SECONDS = 24 * 60 * 60;
/** Suscripción con 100 % de fallas durante 72 h → se desactiva. */
export const WEBHOOK_AUTO_DISABLE_AFTER_SECONDS = 72 * 60 * 60;
/** Entrega de webhook — máquinas de estado §12. `FAILED → PENDING` = reenvío manual (mismo eventId). */
export const WebhookDeliveryMachine = defineStateMachine({
    name: 'WebhookDelivery',
    states: ['PENDING', 'RETRYING', 'SUCCEEDED', 'FAILED'],
    initial: ['PENDING'],
    terminal: ['SUCCEEDED'],
    transitions: {
        PENDING: ['SUCCEEDED', 'RETRYING', 'FAILED'],
        RETRYING: ['SUCCEEDED', 'RETRYING', 'FAILED'],
        SUCCEEDED: [],
        FAILED: ['PENDING'],
    },
});
export const WebhookDeliveryStatusSchema = WebhookDeliveryMachine.schema.meta({
    id: 'WebhookDeliveryStatus',
});
export const WebhookSubscriptionSchema = z
    .object({
    id: WebhookSubscriptionIdSchema,
    url: z.url({ protocol: /^https$/ }),
    events: z.array(EventTypeSchema).min(1),
    description: z.string().nullable(),
    status: z.enum(['ACTIVE', 'DISABLED']),
    createdAt: TimestampSchema,
})
    .meta({ id: 'WebhookSubscription' });
export const CreateWebhookSubscriptionRequestSchema = z
    .object({
    url: z.url({ protocol: /^https$/ }),
    events: z.array(EventTypeSchema).min(1),
    description: z.string().max(200).optional(),
})
    .meta({ id: 'CreateWebhookSubscriptionRequest' });
/** El secreto solo se devuelve al crear o rotar. */
export const CreateWebhookSubscriptionResponseSchema = WebhookSubscriptionSchema.extend({
    secret: z.string(),
});
export const WebhookDeliverySchema = z
    .object({
    id: WebhookDeliveryIdSchema,
    subscriptionId: WebhookSubscriptionIdSchema,
    eventId: EventIdSchema,
    eventType: EventTypeSchema,
    attempt: z.int().min(1),
    status: WebhookDeliveryStatusSchema,
    httpStatus: z.int().nullable(),
    nextAttemptAt: TimestampSchema.nullable(),
    responseExcerpt: z.string().nullable(),
    createdAt: TimestampSchema,
})
    .meta({ id: 'WebhookDelivery' });
/** Fuentes de webhooks entrantes (`integration.inbound_webhooks.source`). */
export const InboundWebhookSourceSchema = z.enum([
    'BREB',
    'CREDIT_CORE',
    'DEPOSIT_CORE',
    'ESIGN',
    'SCA_DELEGATED',
]);
//# sourceMappingURL=envelope.js.map